'use client'

import React from 'react'
import { lodgeDestinationsData, adventuresData, experiencesData } from './headerData'
import { useActivityVisibility, extractIdFromUrl } from '@/hooks/useActivityVisibility'
import { useDynamicActivities } from '@/hooks/useDynamicActivities'

interface HeaderSidebarsProps {
  t: (key: string) => string
  tHeader: (key: string) => string
  tFooter: (key: string) => string
  tLodgesRegions: (key: string) => string
  Link: React.ComponentType<{
    href: string
    className: string
    prefetch: boolean
    children: React.ReactNode
  }>
}

/**
 * Header Sidebars Component
 * Three desktop sidebars: Lodges, Adventures, Menu Principal
 */
export function HeaderSidebars({ t, tHeader, tFooter, tLodgesRegions, Link }: HeaderSidebarsProps) {
  const { isVisible } = useActivityVisibility()
  const { activities: dynamicActivities } = useDynamicActivities()

  // Static adventure slugs for dedup
  const staticSlugs = new Set(adventuresData.map(a => extractIdFromUrl(a.url)))
  // Dynamic-only activities not in the static list
  const dynamicOnly = dynamicActivities.filter(
    a => !staticSlugs.has(a.slug) && !staticSlugs.has(a.id)
  )

  return (
    <>
      {/* Sidebar Overlay */}
      <div className="sidebar-overlay" data-sidebar-overlay />

      {/* Sidebar: Lodges */}
      <div className="sidebar" data-sidebar="lodges">
        <div className="sidebar-content">
          <div className="sidebar-header">
            <h3>{tHeader('lodgeDestinations')}</h3>
          </div>

          <div className="sidebar-body">
            {lodgeDestinationsData
              .filter(region => region.lodges.some(l => isVisible(extractIdFromUrl(l.url), l.comingSoon)))
              .map((region, idx) => (
              <div key={idx} className="sidebar-group">
                <div className="sidebar-region-title">{tLodgesRegions(region.regionKey)}</div>
                <div className="sidebar-lodges-list">
                  {region.lodges.filter(lodge => isVisible(extractIdFromUrl(lodge.url), lodge.comingSoon)).map((lodge, lodgeIdx) => (
                    <Link
                      key={lodgeIdx}
                      href={lodge.url}
                      className={`sidebar-lodge-link${lodge.comingSoon ? ' coming-soon' : ''}`}
                      prefetch={false}
                    >
                      {tFooter(lodge.nameKey)}
                      {lodge.comingSoon && (
                        <span className="coming-soon-badge">
                          {tFooter('comingSoon')}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="sidebar-footer">
            <Link href="/lodges" className="sidebar-view-all" prefetch={false}>
              {t('viewAllLodges')}
            </Link>
          </div>
        </div>
      </div>

      {/* Sidebar: Adventures */}
      <div className="sidebar" data-sidebar="adventures">
        <div className="sidebar-content">
          <div className="sidebar-header">
            <h3>{tHeader('guidedAdventures')}</h3>
          </div>

          <div className="sidebar-body">
            <div className="sidebar-group">
              <div className="sidebar-adventures">
                {adventuresData.filter(a => isVisible(extractIdFromUrl(a.url), a.hidden)).map((adventure, idx) => (
                  <Link key={idx} href={adventure.url} className="sidebar-adventure-link" prefetch={false}>
                    {tFooter(adventure.nameKey)}
                  </Link>
                ))}
                {dynamicOnly.map((activity) => (
                  <Link key={activity.id} href={`/guided-adventures/${activity.slug}`} className="sidebar-adventure-link" prefetch={false}>
                    {activity.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="sidebar-footer">
            <Link href="/guided-adventures" className="sidebar-view-all" prefetch={false}>
              {t('viewAllAdventures')}
            </Link>
          </div>
        </div>
      </div>

      {/* Sidebar: Experiences */}
      <div className="sidebar" data-sidebar="experiences">
        <div className="sidebar-content">
          <div className="sidebar-header">
            <h3>{tHeader('apexExperiences')}</h3>
          </div>

          <div className="sidebar-body">
            <div className="sidebar-group">
              <div className="sidebar-adventures">
                {experiencesData.filter(e => isVisible(extractIdFromUrl(e.url), e.hidden)).map((experience, idx) => (
                  <Link key={idx} href={experience.url} className="sidebar-adventure-link" prefetch={false}>
                    {tFooter(experience.nameKey)}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="sidebar-footer">
            <Link href="/experiences" className="sidebar-view-all" prefetch={false}>
              {t('viewAllExperiences')}
            </Link>
          </div>
        </div>
      </div>

      {/* Sidebar: Menu Principal */}
      <div className="sidebar" data-sidebar="menu-principal">
        <div className="sidebar-content">
          <div className="sidebar-header">
            <h3>{t('menu')}</h3>
          </div>

          <div className="sidebar-body">
            <div className="sidebar-group">
              <div className="sidebar-menu-principal">
                <Link href="/" className="sidebar-menu-link" prefetch={false}>
                  {t('home')}
                </Link>
                <button
                  className="sidebar-menu-link sidebar-menu-lodge-trigger"
                  data-sidebar-trigger="lodges"
                  data-from-menu="true"
                >
                  {t('lodges')}
                  <svg
                    className="region-arrow"
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 1L6 4L2 7"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  className="sidebar-menu-link sidebar-menu-lodge-trigger"
                  data-sidebar-trigger="adventures"
                  data-from-menu="true"
                >
                  {t('adventures')}
                  <svg
                    className="region-arrow"
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 1L6 4L2 7"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  className="sidebar-menu-link sidebar-menu-lodge-trigger"
                  data-sidebar-trigger="experiences"
                  data-from-menu="true"
                >
                  {t('experiences')}
                  <svg
                    className="region-arrow"
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 1L6 4L2 7"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <Link href="/events" className="sidebar-menu-link" prefetch={false}>
                  {t('events')}
                </Link>
                <Link href="/testimonials" className="sidebar-menu-link" prefetch={false}>
                  {t('testimonials')}
                </Link>
                <Link href="/why-apex" className="sidebar-menu-link" prefetch={false}>
                  {t('whyApex')}
                </Link>
                <Link href="/contact" className="sidebar-menu-link" prefetch={false}>
                  {t('contact')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
